import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Badge, Box, Button, Group, Paper, Select, SimpleGrid, Stack, Text} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import type {TagSchema} from 'constants/RegistryConstants';
import {FieldType, type SchemaField} from 'schemas/fields/SchemaFieldConstants';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';

import {
  getModelInputTypeDefinition,
  MODEL_INPUT_FIELD_VALUE_TYPE,
  type ModelInputFieldDefinition,
  type ModelInputTypeDefinition,
} from './CustomEntityInputTypeDefinitions';

export interface CustomEntityInputFieldMapping {
  inputFieldDisplayName: string;
  inputFieldKey: string;
  schemaFieldApiId: string;
  schemaFieldName: string;
  valueType: string;
}

export interface CustomEntityFieldMapping {
  fieldMappings: Array<CustomEntityInputFieldMapping>;
  inputType: string;
  inputTypeDisplayName: string;
  schemaId: string;
  schemaName: string;
}

function isBioentityLinkToFolderItemType(field: SchemaField, folderItemType: string): boolean {
  return (
    field.fieldType === FieldType.BIOENTITY_LINK &&
    field.requiredLink != null &&
    'tagSchema' in field.requiredLink &&
    field.requiredLink.tagSchema?.folderItemType === folderItemType
  );
}

function isSchemaFieldMappableToValueType(field: SchemaField, valueType: string): boolean {
  if (valueType === MODEL_INPUT_FIELD_VALUE_TYPE.AMINO_ACID_SEQUENCE) {
    return (
      field.fieldType === FieldType.AA_PART_LINK ||
      isBioentityLinkToFolderItemType(field, ModelTypeConstants.PROTEIN)
    );
  }
  if (valueType === MODEL_INPUT_FIELD_VALUE_TYPE.MOLECULE) {
    return (
      field.fieldType === FieldType.BASE_MOLECULE_LINK ||
      isBioentityLinkToFolderItemType(field, ModelTypeConstants.MOLECULE)
    );
  }
  return false;
}

export function isSchemaFieldMappableToInputField(
  field: SchemaField,
  inputField: ModelInputFieldDefinition
): boolean {
  return isSchemaFieldMappableToValueType(field, inputField.valueType);
}

export function hasMappableFields(schema: TagSchema, inputType: ModelInputTypeDefinition): boolean {
  const requiredFieldCounts = new Map<string, number>();
  for (const inputField of inputType.fields) {
    requiredFieldCounts.set(inputField.valueType, (requiredFieldCounts.get(inputField.valueType) ?? 0) + 1);
  }
  return [...requiredFieldCounts].every(
    ([valueType, count]) =>
      schema.fields.filter((field) => isSchemaFieldMappableToValueType(field, valueType)).length >= count
  );
}

interface CustomEntitySelectionSetupProps {
  inputTypes: ReadonlyArray<string>;
  onContinue: (mapping: CustomEntityFieldMapping) => void;
}

export default function CustomEntitySelectionSetup({
  inputTypes,
  onContinue,
}: CustomEntitySelectionSetupProps): JSX.Element {
  const [selectedInputTypeValue, setSelectedInputTypeValue] = useState<string | null>(null);
  const [selectedSchema, setSelectedSchema] = useState<TagSchema | null>(null);
  const [selectedSchemaFields, setSelectedSchemaFields] = useState<Record<string, string | null>>({});

  const inputTypeDefinitions = useMemo(
    () =>
      inputTypes.flatMap((inputType) => {
        const definition = getModelInputTypeDefinition(inputType);
        return definition == null ? [] : [definition];
      }),
    [inputTypes]
  );
  const selectedInputType = useMemo(
    () => getModelInputTypeDefinition(selectedInputTypeValue ?? ''),
    [selectedInputTypeValue]
  );
  const inputTypeOptions = useMemo(
    () =>
      inputTypeDefinitions.map((inputType) => ({
        label: inputType.displayName,
        value: inputType.type,
      })),
    [inputTypeDefinitions]
  );

  const handleInputTypeChange = (inputType: string | null): void => {
    setSelectedInputTypeValue(inputType);
    setSelectedSchema(null);
    setSelectedSchemaFields({});
  };

  const handleSchemaChange = (schema: TagSchema | null): void => {
    setSelectedSchema(schema);
    setSelectedSchemaFields({});
  };

  const handleSchemaFieldChange = (inputFieldKey: string, schemaFieldApiId: string | null): void => {
    setSelectedSchemaFields((current) => ({...current, [inputFieldKey]: schemaFieldApiId}));
  };

  const handleContinue = (): void => {
    if (selectedInputType == null || selectedSchema == null) {
      return;
    }
    const fieldMappings = selectedInputType.fields.flatMap((inputField) => {
      const schemaField = selectedSchema.fields.find(
        (field) => field.apiId === selectedSchemaFields[inputField.key]
      );
      return schemaField == null
        ? []
        : [
            {
              inputFieldDisplayName: inputField.displayName,
              inputFieldKey: inputField.key,
              schemaFieldApiId: schemaField.apiId,
              schemaFieldName: schemaField.name,
              valueType: inputField.valueType,
            },
          ];
    });
    if (fieldMappings.length !== selectedInputType.fields.length) {
      return;
    }
    onContinue({
      fieldMappings,
      inputType: selectedInputType.type,
      inputTypeDisplayName: selectedInputType.displayName,
      schemaId: selectedSchema.id,
      schemaName: selectedSchema.name,
    });
  };

  const canContinue =
    selectedInputType != null &&
    selectedSchema != null &&
    selectedInputType.fields.every((inputField) => selectedSchemaFields[inputField.key] != null);

  return (
    <Box {...getTestProps({component: 'CustomEntitySelectionSetup', element: 'root'})}>
      <Stack spacing="lg">
        <Stack spacing={4}>
          <Text size="xl" weight={600}>
            Configure custom entity data
          </Text>
          <Text color="dimmed">
            Choose a model input type and custom entity type, then map the schema fields that provide each
            model input.
          </Text>
        </Stack>

        <Alert color="blue" title="Select once, then work in bulk">
          This mapping applies to every custom entity you select. Each entity must provide exactly one value
          in every mapped field.
        </Alert>

        <Paper component="section" withBorder={true} radius="md" p="lg" aria-label="Model input type">
          <Stack spacing="sm">
            <Text weight={600}>1. Choose a model input type</Text>
            <Text size="sm" color="dimmed">
              The input type defines which named fields need to be mapped.
            </Text>
            <Box maw={480} {...getTestProps({component: 'CustomEntitySelectionSetup', element: 'inputType'})}>
              <Select
                data={inputTypeOptions}
                label="Input type"
                onChange={handleInputTypeChange}
                placeholder="Choose an input type"
                value={selectedInputTypeValue}
              />
            </Box>
          </Stack>
        </Paper>

        <Paper component="section" withBorder={true} radius="md" p="lg" aria-label="Custom entity type">
          <Stack spacing="sm">
            <Text weight={600}>2. Choose a custom entity type</Text>
            <Text size="sm" color="dimmed">
              Your selection limits the browser to entities that use this schema.
            </Text>
            {selectedInputType == null ? (
              <Text color="dimmed">Choose an input type to see compatible custom entity types.</Text>
            ) : (
              <Box
                maw={480}
                {...getTestProps({component: 'CustomEntitySelectionSetup', element: 'schemaSelector'})}
              >
                <TagSchemaSelector
                  allowNone={true}
                  oligoAwareFolderItemTypes={[ModelTypeConstants.BASIC_FOLDER_ITEM]}
                  onChange={handleSchemaChange}
                  selectedTagSchemaId={selectedSchema?.id}
                  tagSchemaFilterFn={(schema) => hasMappableFields(schema, selectedInputType)}
                />
              </Box>
            )}
          </Stack>
        </Paper>

        <Paper component="section" withBorder={true} radius="md" p="lg" aria-label="Field mapping">
          <Stack spacing="md">
            <Stack spacing={4}>
              <Text weight={600}>3. Map schema fields</Text>
              <Text size="sm" color="dimmed">
                Match each field required by {selectedInputType?.displayName ?? 'the input type'} to a field
                on the custom entity.
              </Text>
            </Stack>

            {selectedSchema == null || selectedInputType == null ? (
              <Text color="dimmed">Choose an input type and custom entity type to map fields.</Text>
            ) : (
              <>
                <SimpleGrid
                  cols={Math.min(selectedInputType.fields.length, 2)}
                  spacing="md"
                  breakpoints={[{maxWidth: 'sm', cols: 1}]}
                >
                  {selectedInputType.fields.map((inputField) => {
                    const selectedOtherFieldIds = new Set(
                      Object.entries(selectedSchemaFields).flatMap(([key, fieldId]) =>
                        key !== inputField.key && fieldId != null ? [fieldId] : []
                      )
                    );
                    const options = selectedSchema.fields
                      .filter(
                        (field) =>
                          isSchemaFieldMappableToInputField(field, inputField) &&
                          !selectedOtherFieldIds.has(field.apiId)
                      )
                      .map((field) => ({label: field.name, value: field.apiId}));
                    const handleChange = (schemaFieldApiId: string | null): void => {
                      handleSchemaFieldChange(inputField.key, schemaFieldApiId);
                    };
                    return (
                      <Box
                        key={inputField.key}
                        {...getTestProps({
                          component: 'CustomEntitySelectionSetup',
                          element: 'inputField',
                          key: inputField.key,
                        })}
                        data-amp-safe={false}
                      >
                        <Select
                          clearable={true}
                          data={options}
                          description="Required model input"
                          label={inputField.displayName}
                          nothingFound="No available schema fields"
                          onChange={handleChange}
                          placeholder="Choose a schema field"
                          searchable={true}
                          value={selectedSchemaFields[inputField.key]}
                        />
                      </Box>
                    );
                  })}
                </SimpleGrid>
                <Group spacing="xs">
                  <Text size="sm" color="dimmed">
                    Mapping preview
                  </Text>
                  {selectedInputType.fields.map((inputField) => (
                    <Badge key={inputField.key} variant="light">
                      {inputField.displayName} ←{' '}
                      {selectedSchema.fields.find(
                        (field) => field.apiId === selectedSchemaFields[inputField.key]
                      )?.name ?? 'Not mapped'}
                    </Badge>
                  ))}
                </Group>
              </>
            )}
          </Stack>
        </Paper>

        <Group position="right">
          <Button disabled={!canContinue} onClick={handleContinue} size="md">
            Browse {selectedSchema?.name ?? 'custom entities'}
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
